function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { tokenManager } from '@edusense/shared-auth-core';
import { addConversationMessage as orvalAddConversationMessage, createConversation as orvalCreateConversation, deleteConversation as orvalDeleteConversation, getConversation as orvalGetConversation, listConversations as orvalListConversations, updateConversation as orvalUpdateConversation } from '@edusense/ai-client';
import { customInstance as aiClientInstance } from '@edusense/ai-client/custom-axios';
export class AIServerClient {
    setDefaultUser(userId) {
        this.defaultUserId = userId;
    }
    normalizeModel(model) {
        const m = (model || '').trim();
        if (!m || m === 'auto' || m === 'default') return 'gpt-oss:20b';
        return m;
    }
    getAuthToken() {
        // TokenManager에서 JWT 토큰 우선 사용 (내부 시스템), 없으면 API 키 사용 (외부 사용)
        const jwtToken = tokenManager.getToken() || localStorage.getItem('access_token');
        const token = jwtToken || this.apiKey;
        // 토큰 타입 감지
        let tokenType = 'API_KEY';
        if (token.startsWith('sk-')) {
            tokenType = 'API_KEY';
        } else if (token.includes('.') && token.split('.').length === 3) {
            tokenType = 'JWT';
        } else {
            tokenType = 'OPAQUE_TOKEN';
        }
        // 디버깅용 로그
        console.log('🔐 Auth Token Debug:', {
            hasJwtToken: !!jwtToken,
            jwtTokenPrefix: jwtToken ? jwtToken.substring(0, 20) + '...' : 'null',
            apiKeyPrefix: this.apiKey ? this.apiKey.substring(0, 20) + '...' : 'null',
            finalTokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
            tokenType: tokenType
        });
        return token;
    }
    async refreshTokenIfNeeded() {
        console.log('🔄 Attempting token refresh via TokenManager...');
        return await tokenManager.refreshTokenIfNeeded();
    }
    async fallbackChatV2(request, token) {
        const model = this.normalizeModel(request.model);
        const tryBases = [];
        tryBases.push(this.baseUrl);
        try {
            const stored = typeof window !== 'undefined' ? localStorage.getItem('ai_base_fallback') : null;
            if (stored) tryBases.push(stored);
        } catch  {}
        // As a last resort, hardcode the canonical domain
        if (!tryBases.includes('https://ai.ugot.uk')) tryBases.push('https://ai.ugot.uk');
        for (const base of tryBases){
            try {
                const resp = await fetch(`${base}/api/v2/chat`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...token ? {
                            'Authorization': `Bearer ${token}`
                        } : {}
                    },
                    body: JSON.stringify({
                        model,
                        messages: request.messages,
                        maxTokens: 2000
                    })
                });
                if (!resp.ok) {
                    const errText = await resp.text().catch(()=>'');
                    console.warn(`v2 chat fallback failed at ${base}:`, resp.status, errText);
                    continue;
                }
                const data = await resp.json().catch(()=>({}));
                const content = data?.response || data?.message || data?.choices?.[0]?.message?.content || data?.content || '';
                if (content && content.length > 0) return content;
            } catch (e) {
                console.warn(`v2 chat fallback network error at base ${base}:`, e);
            }
        }
        return '';
    }
    async createConversation(input, context) {
        const payload = input && Object.keys(input).length > 0 ? input : undefined;
        try {
            return await orvalCreateConversation(payload);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return this.buildLocalConversationSummary(payload, context);
            }
            throw error;
        }
    }
    async listConversations(params) {
        try {
            return await orvalListConversations(params);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return [];
            }
            throw error;
        }
    }
    async getConversation(conversationId) {
        if (this.isLocalConversationId(conversationId)) {
            return this.buildLocalConversationDetail(conversationId);
        }
        try {
            return await orvalGetConversation(conversationId);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return this.buildLocalConversationDetail(conversationId);
            }
            throw error;
        }
    }
    async updateConversationTitle(conversationId, title) {
        const request = {
            title
        };
        if (this.isLocalConversationId(conversationId)) {
            return this.buildLocalConversationSummary({
                title
            }, {
                conversationId
            });
        }
        try {
            return await orvalUpdateConversation(conversationId, request);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return this.buildLocalConversationSummary({
                    title
                }, {
                    conversationId
                });
            }
            throw error;
        }
    }
    async deleteConversation(conversationId) {
        if (this.isLocalConversationId(conversationId)) {
            return;
        }
        try {
            await orvalDeleteConversation(conversationId);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return;
            }
            throw error;
        }
    }
    async clearConversationMessages(conversationId) {
        if (this.isLocalConversationId(conversationId)) {
            const summary = this.buildLocalConversationSummary(undefined, {
                conversationId,
                userId: this.defaultUserId
            });
            return {
                ...summary,
                messageCount: 0,
                lastMessagePreview: null,
                lastMessageRole: undefined,
                lastMessageAt: null
            };
        }
        try {
            return await aiClientInstance({
                url: `/conversations/${conversationId}/messages`,
                method: 'DELETE'
            });
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return this.buildLocalConversationSummary(undefined, {
                    conversationId,
                    userId: this.defaultUserId
                });
            }
            throw error;
        }
    }
    async addConversationMessage(conversationId, message) {
        if (this.isLocalConversationId(conversationId)) {
            return this.buildLocalConversationSummary(undefined, {
                conversationId
            });
        }
        try {
            return await orvalAddConversationMessage(conversationId, message);
        } catch (error) {
            if (this.isNotFoundError(error)) {
                return this.buildLocalConversationSummary(undefined, {
                    conversationId
                });
            }
            throw error;
        }
    }
    async *streamChat(request, context) {
        try {
            const token = this.getAuthToken();
            this.abortController?.abort();
            this.abortController = new AbortController();
            // LiteLLM OpenAI-compatible streaming endpoint (no fallback)
            const llmReq = {
                model: this.normalizeModel(request.model),
                messages: request.messages.map((m)=>({
                        role: m.role,
                        content: m.content
                    })),
                stream: true,
                max_tokens: 2000,
                maxTokens: 2000,
                temperature: 0.7
            };
            if (context?.userId) {
                llmReq.user = context.userId;
            }
            console.log('🔍 ai-server-client: Making fetch request to:', `${this.baseUrl}/v1/litellm/chat/completions/stream`);
            const baseHeaders = {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Authorization': `Bearer ${token}`
            };
            let v1resp = await fetch(`${this.baseUrl}/v1/litellm/chat/completions/stream`, {
                method: 'POST',
                headers: baseHeaders,
                body: JSON.stringify(llmReq),
                signal: this.abortController.signal
            });
            console.log('🔍 ai-server-client: Received response:', v1resp.status, v1resp.statusText);
            console.log('🔍 ai-server-client: Response headers:', Object.fromEntries(v1resp.headers.entries()));
            if (!v1resp.ok) {
                const errorText = await v1resp.text().catch(()=>'');
                console.log('❌ ai-server-client: HTTP error response text:', errorText);
                // 401 에러인 경우 토큰 갱신 시도
                if (v1resp.status === 401) {
                    console.log('🔄 ai-server-client: 401 detected, attempting token refresh...');
                    const refreshSuccess = await this.refreshTokenIfNeeded();
                    if (refreshSuccess) {
                        console.log('✅ ai-server-client: Token refreshed, retrying request...');
                        // 새 토큰으로 재시도
                        const newToken = this.getAuthToken();
                        const retryHeaders = {
                            ...baseHeaders,
                            Authorization: `Bearer ${newToken}`
                        };
                        const retryResp = await fetch(`${this.baseUrl}/v1/litellm/chat/completions/stream`, {
                            method: 'POST',
                            headers: retryHeaders,
                            body: JSON.stringify(llmReq),
                            signal: this.abortController?.signal
                        });
                        if (retryResp.ok) {
                            // 성공적으로 갱신된 토큰으로 요청 성공, 새 응답으로 계속 진행
                            v1resp = retryResp;
                        } else {
                            const retryErrorText = await retryResp.text().catch(()=>'');
                            throw new Error(`HTTP error after token refresh! status: ${retryResp.status}, message: ${retryErrorText}`);
                        }
                    } else {
                        throw new Error(`HTTP error! status: ${v1resp.status}, message: ${errorText} (token refresh failed)`);
                    }
                } else {
                    // Always-stream mode: do not fallback to non-stream
                    throw new Error(`HTTP error! status: ${v1resp.status}, message: ${errorText}`);
                }
            }
            console.log('🔍 ai-server-client: Response body available:', !!v1resp.body);
            const reader = v1resp.body?.getReader();
            console.log('🔍 ai-server-client: Stream reader available:', !!reader);
            if (!reader) throw new Error('litellm stream reader not available');
            const decoder = new TextDecoder();
            let buffer = '';
            let aggregated = '';
            let onlyDefault = true;
            const defaultPhrase = '안녕하세요! AI 어시스턴트입니다. 무엇을 도와드릴까요?';
            try {
                console.log('🔍 ai-server-client: Starting SSE stream reading');
                let receivedDone = false;
                while(true){
                    const { done, value } = await reader.read();
                    console.log('🔍 ai-server-client: Read chunk, done:', done, 'value length:', value?.length);
                    if (done) break;
                    const chunk = decoder.decode(value, {
                        stream: true
                    });
                    console.log('🔍 ai-server-client: Decoded chunk:', chunk);
                    buffer += chunk;
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    console.log('🔍 ai-server-client: Processing', lines.length, 'lines');
                    for (const line of lines){
                        console.log('🔍 ai-server-client: Processing line:', JSON.stringify(line));
                        if (!line.trim()) {
                            console.log('⚠️ ai-server-client: Empty line, skipping');
                            continue;
                        }
                        if (!line.startsWith('data:')) {
                            console.log('⚠️ ai-server-client: Line does not start with "data:", skipping:', line);
                            continue;
                        }
                        let data;
                        if (line.startsWith('data:data: ')) {
                            data = line.slice(11).trim();
                            console.log('🔧 ai-server-client: Handled double prefix, extracted data:', JSON.stringify(data));
                        } else if (line.startsWith('data: ')) {
                            data = line.slice(6).trim();
                            console.log('🔍 ai-server-client: Extracted data (normal prefix):', JSON.stringify(data));
                        } else {
                            console.log('⚠️ ai-server-client: Malformed data line:', line);
                            continue;
                        }
                        while(data.startsWith('data: ')){
                            console.log('🔧 ai-server-client: Removing double "data: " prefix');
                            data = data.slice(6).trim();
                        }
                        console.log('🔍 ai-server-client: Final data for parsing:', JSON.stringify(data));
                        const payloads = data.split(/\s+data:\s+/).map((part)=>part.trim()).filter((part)=>part.length > 0);
                        const candidates = payloads.length > 0 ? payloads : [
                            data.trim()
                        ];
                        for (const payload of candidates){
                            if (!payload) {
                                console.log('⚠️ ai-server-client: Empty payload, skipping');
                                continue;
                            }
                            if (payload === '[DONE]') {
                                console.log('✅ ai-server-client: Received [DONE] signal');
                                receivedDone = true;
                                buffer = '';
                                break;
                            }
                            try {
                                const parsed = JSON.parse(payload);
                                console.log('🔍 Parsed SSE data:', parsed);
                                const choice = parsed?.choices?.[0];
                                if (choice?.delta?.content) {
                                    console.log('✅ Yielding content:', choice.delta.content);
                                    aggregated += choice.delta.content;
                                    if (!choice.delta.content.includes('오류') && choice.delta.content.trim() !== defaultPhrase.trim()) {
                                        onlyDefault = false;
                                    }
                                    yield {
                                        model: parsed.model || llmReq.model,
                                        message: {
                                            role: 'assistant',
                                            content: choice.delta.content
                                        },
                                        done: false
                                    };
                                } else {
                                    console.log('⚠️ No content in delta:', choice);
                                }
                                if (choice?.finish_reason) {
                                    receivedDone = true;
                                    yield {
                                        model: parsed.model || llmReq.model,
                                        message: {
                                            role: 'assistant',
                                            content: ''
                                        },
                                        done: true,
                                        done_reason: choice.finish_reason
                                    };
                                    break;
                                }
                                const content = parsed?.message?.content || parsed?.content || parsed?.response;
                                const doneFlag = parsed?.done === true;
                                if (content) {
                                    aggregated += content;
                                    if (!content.includes('오류') && content.trim() !== defaultPhrase.trim()) {
                                        onlyDefault = false;
                                    }
                                    yield {
                                        model: parsed.model || llmReq.model,
                                        message: {
                                            role: 'assistant',
                                            content
                                        },
                                        done: false
                                    };
                                }
                                if (doneFlag) {
                                    receivedDone = true;
                                    yield {
                                        model: parsed.model || llmReq.model,
                                        message: {
                                            role: 'assistant',
                                            content: ''
                                        },
                                        done: true,
                                        done_reason: 'stop'
                                    };
                                    break;
                                }
                            } catch  {
                            // ignore non-json
                            }
                        }
                        if (receivedDone) break;
                    }
                    if (receivedDone) break;
                }
            } finally{
                reader.releaseLock();
            }
            // After stream end:
            // - If 아무 토큰도 받지 못했다면 에러로 처리(상위 레이어가 사용자 메시지를 표시)
            if (aggregated.trim().length === 0) {
                throw new Error('empty_stream');
            }
            // Close the stream
            yield {
                model: llmReq.model,
                message: {
                    role: 'assistant',
                    content: ''
                },
                done: true,
                done_reason: 'stop'
            };
            return;
        } catch (error) {
            console.error('AI chat stream error (no non-stream fallback):', error);
            throw error;
        }
    }
    async chat(request) {
        try {
            const token = this.getAuthToken();
            console.log('OpenAI Compatible Chat Token 확인:', token ? `${token.substring(0, 10)}...` : 'null');
            // LiteLLM non-stream endpoint (no fallback)
            const openAIRequest = {
                model: this.normalizeModel(request.model),
                messages: request.messages.map((msg)=>({
                        role: msg.role,
                        content: msg.content
                    })),
                stream: false,
                max_tokens: 2000,
                temperature: 0.7
            };
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            // Call LiteLLM
            const resp1 = await fetch(`${this.baseUrl}/v1/litellm/chat/completions`, {
                method: 'POST',
                headers,
                body: JSON.stringify(openAIRequest)
            });
            if (!resp1.ok) {
                const errorText = await resp1.text().catch(()=>'');
                console.error(`OpenAI API Error (${resp1.status}):`, errorText);
                // 401 에러인 경우 토큰 갱신 시도
                if (resp1.status === 401) {
                    console.log('🔄 ai-server-client: 401 detected in non-streaming, attempting token refresh...');
                    const refreshSuccess = await this.refreshTokenIfNeeded();
                    if (refreshSuccess) {
                        console.log('✅ ai-server-client: Token refreshed, retrying non-streaming request...');
                        const newToken = this.getAuthToken();
                        const retryHeaders = {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${newToken}`
                        };
                        const retryResp = await fetch(`${this.baseUrl}/v1/litellm/chat/completions`, {
                            method: 'POST',
                            headers: retryHeaders,
                            body: JSON.stringify(openAIRequest)
                        });
                        if (retryResp.ok) {
                            const data = await retryResp.json();
                            const content = data.choices?.[0]?.message?.content || '';
                            if (content && content.length > 0) return content;
                        } else {
                            const retryErrorText = await retryResp.text().catch(()=>'');
                            console.error(`Retry failed after token refresh (${retryResp.status}):`, retryErrorText);
                        }
                    }
                }
                // Fallback to v2 chat endpoint
                const fallback = await this.fallbackChatV2(request, token);
                if (fallback && fallback.length > 0) return fallback;
                throw new Error(`HTTP error! status: ${resp1.status}, message: ${errorText}`);
            }
            const data = await resp1.json();
            const content = data.choices?.[0]?.message?.content || '';
            if (content && content.length > 0) return content;
            // If empty, try v2 chat as backup
            return await this.fallbackChatV2(request, token);
        } catch (error) {
            console.error('OpenAI Compatible Chat error:', error);
            // Fallback
            const token = this.getAuthToken();
            const fallback = await this.fallbackChatV2(request, token);
            if (fallback && fallback.length > 0) return fallback;
            throw error;
        }
    }
    async *streamChatSSE(request) {
        try {
            // streamChat을 통해 SSE 처리하고 content만 추출
            for await (const chunk of this.streamChat(request)){
                if (!chunk.done && chunk.message.content) {
                    yield chunk.message.content;
                }
            }
        } catch (error) {
            console.error('Stream SSE error:', error);
            throw error;
        }
    }
    isNotFoundError(error) {
        return Boolean(error?.response?.status === 404);
    }
    isLocalConversationId(conversationId) {
        return typeof conversationId === 'string' && conversationId.startsWith('local-');
    }
    buildLocalConversationSummary(input, context) {
        const nowIso = new Date().toISOString();
        const generatedId = context?.conversationId ?? `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        return {
            id: generatedId,
            userId: context?.userId ?? 'local-user',
            title: input?.title ?? null,
            model: input?.model ?? null,
            createdAt: nowIso,
            updatedAt: nowIso,
            messageCount: null,
            totalTokens: null,
            lastMessagePreview: null,
            lastMessageRole: undefined,
            lastMessageAt: null
        };
    }
    buildLocalConversationDetail(conversationId) {
        return {
            ...this.buildLocalConversationSummary(undefined, {
                conversationId
            }),
            messages: []
        };
    }
    stopGeneration() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
            console.log('OpenAI Compatible API generation stopped');
        }
    }
    isGenerating() {
        return this.abortController !== null;
    }
    constructor(baseUrl, apiKey){
        _define_property(this, "baseUrl", void 0);
        _define_property(this, "abortController", null);
        _define_property(this, "apiKey", void 0);
        _define_property(this, "defaultUserId", void 0);
        // AI 서버 베이스 URL 설정 (환경변수 > 파라미터 > 기본값)
        const envBase = baseUrl || import.meta?.env?.VITE_AISERVER_URL || (typeof process !== 'undefined' ? process.env?.AI_API_BASE_URL : undefined) || 'https://ai.ugot.uk/api/ai';
        // 후행 슬래시 제거
        this.baseUrl = envBase.replace(/\/$/, '');
        // OpenAI 호환 API 키 사용 (기본값: 테스트 키)
        this.apiKey = apiKey || import.meta.env.VITE_OPENAI_API_KEY || 'sk-test-4e7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c';
    }
}
export const aiServerClient = new AIServerClient();
